<template>
  <PrimeSelect
    ref="selectRef"
    v-model="modelValue"
    unstyled
    :pt="passThrough"
    :loading="loading"
    :options="filteredOptions"
    :placeholder="placeholder"
    :appendTo="appendTo"
    :optionGroupLabel="optionGroupLabel"
    :optionGroupChildren="optionGroupChildren"
    :virtual-scroller-options="
      virtualScrollerOptions
        ? {
            ...virtualScrollerOptions,
            onLazyLoad: virtualLazyLoad,
            loading: loading,
            items: filteredOptions,
          }
        : undefined
    "
    :disabled="disabled"
    :class="[sizeClass, stateClass]"
    class="meridia-select"
    v-bind="$attrs"
    @show="() => emit('show')"
    @hide="() => emit('hide')"
    @clear="() => emit('clear')"
    @blur="() => emit('blur')"
    @focus="() => emit('focus')"
  >
    <template #dropdownicon>
      <transition name="fade">
        <MeridiaIcon
          v-if="modelValue && !clearDisabled"
          class="meridia-select-icon-clear"
          @click="modelValue = undefined"
          name="cancel"
          type="material-icons"
        />
      </transition>
      <MeridiaIcon
        class="meridia-select-dropdown-icon"
        :class="[
          {
            'rotate-180': selectRef?.overlayVisible,
          },
          getPt <
            PassThroughMeridiaSingleSelect >
            ('dropdownIcon', passThrough)?.class,
        ]"
        name="keyboard_arrow_down"
        type="material-icons"
      />
    </template>
    <template #value="scope">
      <div v-if="!scope.value">
        {{ scope.placeholder }}
      </div>
      <div
        v-else
        class="whitespace-nowrap flex gap-2xs overflow-auto scrollbar-thin scrollbar-thumb-slate-400 scrollbar-track-transparent"
      >
        <div v-if="content === SelectContent.TEXT">{{ scope.value.label }}</div>
        <MeridiaChip
          v-else-if="content === SelectContent.CHIPS"
          :label="scope.value.label"
          icon="visibility"
          removable
          @remove-clicked="modelValue = undefined"
        />
        <MeridiaTag
          v-else-if="content === SelectContent.TAGS"
          :type="'risk_level'"
          :label="scope.value.label"
          :state="'low'"
        />
      </div>
    </template>
    <template #header>
      <div
        v-if="!filteringDisabled"
        class="meridia-select-overlay-header"
        v-bind="
          getPt < PassThroughMeridiaSingleSelect > ('header', passThrough)
        "
      >
        <MeridiaInput
          v-model="filter"
          placeholder="Search"
          icon="search"
          class="w-full"
          :pt="
            getPt <
            PassThroughMeridiaSingleSelect >
            ('filterInput', passThrough)
          "
        />
      </div>
    </template>
    <template #option="option">
      <MeridiaRadioButton
        v-if="type === SingleSelectType.SINGLE_WITH_RADIO"
        :model-value="option.selected ? option.option.value : undefined"
        :value="option.option.value"
        name="'aaaa'"
        :label="option.option.label"
        :size="'small'"
        @click.prevent
        :class="{
          'bg-primary-surface-muted': option.selected,
        }"
        class="p-s cursor-pointer transition-all duration-200 w-full h-full hover:bg-neutral-surface-muted"
        v-bind="
          getPt <
          PassThroughMeridiaSingleSelect >
          ('meridiaOption', passThrough, option)
        "
      >
        <template v-if="slots.option" #label>
          <slot
            name="option"
            :option="option.option"
            :selected="option.selected"
            :index="option.index"
          />
        </template>
      </MeridiaRadioButton>
      <div
        v-else
        class="text-body-base text-neutral-text-body p-s cursor-pointer transition-all duration-200 w-full h-full hover:bg-neutral-surface-muted"
        :class="{
          'bg-primary-surface-muted': option.selected,
        }"
        v-bind="
          getPt <
          PassThroughMeridiaSingleSelect >
          ('meridiaOption', passThrough, option)
        "
      >
        {{ option.option.label }}
      </div>
    </template>
    <template #footer="{ value }">
      <div v-if="!clearDisabled" class="flex justify-end p-s">
        <MeridiaButton
          label="Clear"
          :disabled="!value"
          :size="'mini'"
          :type="'tertiary'"
          icon="filter_alt_off"
          class="w-full"
          @click="footerClearClicked"
        />
      </div>
    </template>
    <template #empty>
      <div v-if="error">Error</div>
      <div v-else class="meridia-select-no-options">No options found</div>
    </template>
    <template v-if="slots.optiongroup" #optiongroup="option">
      <slot name="optiongroup" :option="option.option" :index="option.index" />
    </template>
  </PrimeSelect>
</template>

<script setup lang="ts" generic="T">
import { computed, Ref, ref, watch } from 'vue';
import {
  SelectSize,
  SelectContent,
  SelectOption,
  SelectCommonProps,
} from '../types';
import { usePassThrough } from 'primevue/passthrough';
import MeridiaRadioButton from '../../radioButton/MeridiaRadioButton.vue';
import MeridiaInput from '../../input/MeridiaInput.vue';
import MeridiaButton from '../../button/MeridiaButton.vue';
import MeridiaChip from '../../chip/MeridiaChip.vue';
import MeridiaTag from '../../tag/MeridiaTag.vue';
import { PassThroughMeridiaSingleSelect, SingleSelectType } from './types';
import PrimeSelect, { SelectSlots } from 'primevue/select';
import { useSelectUtils } from '../useSelects';
import { getPt } from '../../utils';
import MeridiaIcon from '../../icon/MeridiaIcon.vue';

const modelValue = defineModel<SelectOption<T> | undefined>({ required: true });

const props = withDefaults(
  defineProps<
    {
      type?: `${SingleSelectType}`;
      pt?: PassThroughMeridiaSingleSelect;
    } & SelectCommonProps<T>
  >(),
  {
    size: SelectSize.SMALL,
    type: SingleSelectType.SINGLE_WITH_RADIO,
    filteringDisabled: false,
    clearDisabled: false,
    disabled: false,
    content: SelectContent.TEXT,
    placeholder: 'Select',
    appendTo: 'body',
  }
);

const emit = defineEmits<{
  (event: 'update:model-value', value: SelectOption<T>): void;
  /**
   * fired when user clears the input
   */
  (event: 'clear'): void;
  /**
   * fired when user focuses on the input
   */
  (event: 'focus'): void;
  /**
   * fired when user blurs the input
   */
  (event: 'blur'): void;
  /**
   * fired when user opens the dropdown
   */
  (event: 'show'): void;
  /**
   * fired when user closes the dropdown
   */
  (event: 'hide'): void;
}>();

const slots = defineSlots<
  {
    option: (option: {
      option: SelectOption<T>;
      selected: boolean;
      index: number;
    }) => any;
  } & Pick<SelectSlots, 'optiongroup'>
>();

const filter = ref('');
const loading = ref(false);
const selectRef = ref<InstanceType<typeof PrimeSelect> & any>(null);
const error = ref(false);
const totalCount = ref<number>(props.virtualScrollerOptions?.step ?? 30);
const filteredOptions = ref<SelectOption<T>[]>(props.options);

const { virtualLazyLoad, lazyFetchData } = useSelectUtils<T>(
  props,
  filteredOptions as Ref<SelectOption<T>[]>,
  filter,
  totalCount,
  loading,
  error
);

// Update filtered options when options change
watch(
  () => props.options,
  () => {
    filteredOptions.value = [...props.options];
  }
);

const sizeClass = computed(() => ({
  'size-small': props.size === SelectSize.SMALL,
  'size-large': props.size === SelectSize.LARGE,
}));

const stateClass = computed(() => ({
  'state-disabled': props.disabled,
  'state-seelected': modelValue.value,
}));

const passThrough = usePassThrough(
  props.pt,
  {
    dropdown: {
      class: 'meridia-select-dropdown-container',
    },
    label: {
      class: 'meridia-select-placeholder h-min flex-grow overflow-hidden',
    },
    overlay: {
      class: 'meridia-select-overlay',
    },
    pcheadercheckbox: {
      root: {
        class: 'hidden',
      },
    },
    pcOptionCheckbox: {
      root: {
        class: 'hidden',
      },
    },
    listContainer: {
      class:
        'overflow-y-auto scrollbar-thin scrollbar-thumb-slate-400 scrollbar-track-transparent',
    },
  } as PassThroughMeridiaSingleSelect,
  { mergeSections: true, mergeProps: true }
) as PassThroughMeridiaSingleSelect;

function footerClearClicked() {
  selectRef.value.onClearClick();
  selectRef.value.hide();
}

defineExpose({
  lazyFetchData,
});
</script>

<style src="../select.scss"></style>
